import { useTranslation } from "react-i18next";
import { useMediaQuery } from "react-responsive";
import helpers from "../../helpers/helpers";
import { fireTagManagerEvent } from "../../../assets/Services/tagmanager";
import { COLLECTION_TYPE } from "../../../assets/models/iCollection";
import { iWallSlot, WALL_SLOT_TYPE } from "../../../config/wall";
import { iNft } from "../../../assets/models/iNft";
import { useEffect, useState } from "react";
import NftController from "../../../assets/Controllers/NftController";
import { apiErrorToast } from "../../../assets/Utils/errors";
import { SMALL_SCREEN, datetimeRangeToString } from "../../../assets/Utils/generic";
import {
  getAmountOfGuests,
  getCityFromLocation,
  getRegionFromLocation,
} from "../../../assets/Utils/location";
import { openNftDetailPage } from "../TakPage/TakPage";

const LIGHT_DOTS_STYLE = {
  backgroundImage:
    "radial-gradient(circle, rgba(9, 17, 18, 0.08) 1px, transparent 1px)",
  backgroundSize: "20px 20px",
} as const;

const LIGHT_VIGNETTE_STYLE = {
  backgroundImage:
    "radial-gradient(80% 140% at 50% -20%, rgba(255, 255, 255, 0.95) 20%, rgba(255, 255, 255, 0.15) 70%, rgba(255, 255, 255, 0) 100%)",
} as const;

export default function SecondaryMarket() {
  const { t, i18n } = useTranslation();
  const isSmallScreen = useMediaQuery({ query: SMALL_SCREEN });
  const mixedTakgetInspired = {
    $or: [
      {
        $and: [
          {
            $or: [
              { "payload.type": { $exists: false } },
              { "payload.type": "hotel" },
              { "payload.type": null },
            ],
          },
          {
            $or: [
              { isHotelFakeSupply: { $exists: false } },
              { isHotelFakeSupply: false },
            ],
          },
        ],
      },
      {
        $and: [
          {
            $or: [
              { "payload.type": { $exists: false } },
              { "payload.type": "hotel" },
              { "payload.type": null },
            ],
          },
          {
            isHotelFakeSupply: true,
          },
        ],
      },
      {
        "payload.type": COLLECTION_TYPE.experience,
      },
    ],
    sellStatus: "FOR_SALE",
  };

  const toCurrency = (price?: number) => {
    if (!price || Number.isNaN(price)) return "€0";

    const locale =
      i18n.language === "it"
        ? "it-IT"
        : i18n.language === "es"
        ? "es-ES"
        : "en-US";

    return `€${new Intl.NumberFormat(locale, {
      maximumFractionDigits: 0,
    }).format(price)}`;
  };

  const getLocationLabel = (nft: iNft) => {
    const location = nft._collectionCensus?.location;
    const city = getCityFromLocation(location);
    const region = getRegionFromLocation(location);

    if (city && region) return `${city} (${region})`;
    if (city) return city;
    return nft._collectionName ?? "";
  };

  const openCard = (nft: iNft) => {
    fireTagManagerEvent("open_card", {
      nft_id: nft._id,
      source:
        window.location.pathname === "/" ? "home" : window.location.pathname,
    });

    openNftDetailPage(nft._id);
  };

  const stays: iWallSlot = {
    title: "secondary.stays_title",
    config: {
      type: WALL_SLOT_TYPE.CLASSIC,
      payload: {
        scroll: false,
        query: mixedTakgetInspired,
        sort: { isHotelFakeSupply: 1 },
      },
    },
  };

  const [isLoading, setIsLoading] = useState(true);
  const [nfts, setNfts] = useState<iNft[]>([]);

  const loadNfts = async () => {
    setIsLoading(true);
    try {
      // Use type assertion to access the payload properties
      const payload = stays.config.payload as { sort: any; query: any };

      const data = await NftController.getPaginated({
        page: 1,
        sort: JSON.stringify(payload.sort),
        query: JSON.stringify(payload.query),
      });

      setNfts(data.data);
    } catch (error) {
      apiErrorToast(error);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    loadNfts();
  }, []);

  return (
    <main className="relative isolate overflow-hidden bg-white" style={{ minHeight: "100svh" }}>
      <div className="pointer-events-none absolute inset-0" style={LIGHT_DOTS_STYLE} />
      <div className="pointer-events-none absolute inset-0 z-[1]" style={LIGHT_VIGNETTE_STYLE} />
      <section
        className="relative z-[2] mx-auto w-full max-w-[1200px] px-5 lg:px-4"
        style={{
          paddingTop: `${(isSmallScreen ? 104 : 88) + (isSmallScreen ? 100 : 120)}px`,
          paddingBottom: isSmallScreen ? "100px" : "120px",
        }}
      >
        <div
          className="mx-auto flex w-full max-w-[830px] flex-col items-start gap-5 lg:items-center lg:gap-4 lg:text-center"
        >
          <div className="inline-flex h-9 items-center justify-center gap-2 rounded-[100px] border border-[rgba(9,17,18,0.1)] bg-[rgba(255,255,255,0.1)] py-[6px] pl-[6px] pr-[10px] shadow-[0_2px_4px_rgba(9,17,18,0.06)] backdrop-blur-[50px]">
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[#091112] text-white">
              <i className="material-symbols-rounded" style={{ fontSize: "16px" }}>
                sync_alt
              </i>
            </span>
            <span className="[font-family:'Geist',_sans-serif] text-[16px] font-normal leading-5 text-[#091112]">
              {t("secondary.badge")}
            </span>
          </div>

          <div className="flex w-full flex-col items-start gap-4 lg:items-center">
            <p
              className="m-0 w-full [font-family:'Geist',_sans-serif] text-[30px] font-medium leading-[32px] tracking-[-0.9px] text-[#091112] lg:max-w-[830px] lg:text-[48px] lg:leading-[52px] lg:tracking-[-1.44px]"
            >
              {t(stays.title ?? "")}
            </p>
            <p
              className="m-0 w-full [font-family:'Geist',_sans-serif] text-[16px] font-normal leading-[22px] tracking-[-0.32px] text-[rgba(9,17,18,0.6)] lg:max-w-[596px] lg:text-[18px] lg:leading-6 lg:tracking-[-0.36px]"
            >
              {t("ds.section_2_subtitle")}
            </p>
          </div>
        </div>

        <div className="mx-auto mt-10 w-full max-w-[1040px] lg:mt-[60px]">
          {isLoading ? (
            <div className="flex items-center justify-center py-10">
              <p className="m-0 [font-family:'Geist',_sans-serif] text-[16px] leading-[22px] text-[rgba(9,17,18,0.6)]">
                ...
              </p>
            </div>
          ) : (
            <div className="flex w-full flex-col gap-5 lg:grid lg:grid-cols-[repeat(auto-fill,320px)] lg:justify-center lg:gap-x-10 lg:gap-y-10">
              {nfts.map((nft, index) => {
                const bgImage = nft?.images?.[0] ?? nft?._collectionImages?.[0];
                const oldPrice = helpers.getOldPrice(nft);
                const currentPrice = helpers.getCurrentPrice(nft);
                const guests = getAmountOfGuests(nft);
                const guestCount = (guests.adults ?? 0) + (guests.childs ?? 0);

                return (
                  <article
                    key={`secondary-market-card-${nft._id}-${index}`}
                    className="w-full cursor-pointer overflow-hidden rounded-[24px] border border-[#E8E8E8] bg-white pb-4 shadow-[0_4px_10px_rgba(9,17,18,0.1)] lg:w-[320px]"
                    onClick={() => openCard(nft)}
                  >
                    <div className="h-[206px] w-full overflow-hidden rounded-t-[24px] bg-[#f5f5f5]">
                      {bgImage ? (
                        <img
                          src={bgImage}
                          alt={nft._collectionName ?? t("retrieve.title2")}
                          className="size-full object-cover"
                        />
                      ) : null}
                    </div>

                    <div className="flex w-full flex-col gap-[2px] px-5 pt-4">
                      <p className="m-0 [font-family:'Geist',_sans-serif] text-[16px] font-semibold leading-[22px] text-[#091112]">
                        {getLocationLabel(nft)}
                      </p>
                      <p className="m-0 [font-family:'Geist',_sans-serif] text-[16px] font-normal leading-[22px] text-[#091112]">
                        {nft._collectionName}
                      </p>
                      <p className="m-0 [font-family:'Geist',_sans-serif] text-[16px] font-normal leading-[22px] text-[#091112]">
                        {datetimeRangeToString(
                          nft?.payload?.checkin,
                          nft?.payload?.checkout,
                          nft._collectionCensus?.location?.gmt,
                        ) ?? "-"}
                      </p>
                      <p className="m-0 [font-family:'Geist',_sans-serif] text-[16px] font-normal leading-[22px] text-[#091112]">
                        {guestCount} {t("wall.guests")}
                      </p>

                      <div className="flex items-baseline gap-1 pb-3 pt-3">
                        <p className="m-0 [font-family:'Geist',_sans-serif] text-[32px] font-medium leading-8 tracking-[-0.64px] text-[#091112]">
                          {toCurrency(Number(currentPrice))}
                        </p>
                        {oldPrice !== undefined && oldPrice > Number(currentPrice) ? (
                          <p className="m-0 [font-family:'Geist',_sans-serif] text-[16px] font-normal leading-4 text-[#747474] line-through">
                            {toCurrency(Number(oldPrice))}
                          </p>
                        ) : null}
                      </div>
                    </div>

                    <div className="h-px w-full bg-[#E8E8E8]" />

                    <div className="px-4 pt-4">
                      <button
                        type="button"
                        className="flex h-12 w-full items-center justify-center rounded-xl border-0 bg-[#091112] [font-family:'Geist',_sans-serif] text-[16px] font-medium leading-[22px] text-white shadow-[0_4px_10px_rgba(0,0,0,0.05)]"
                        onClick={(event) => {
                          event.stopPropagation();
                          openCard(nft);
                        }}
                      >
                        {t("nftpage.offer")}
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </main>
  );
}
